import { isNotEmptyString } from 'src/app/constant/constant-function';
import { COOKIE_KEYS, LAST_ACTIVITY_KEY, LOGGED_OUT_VALUE } from 'src/app/constant/constant';
import { CookieService } from 'ngx-cookie-service';
import { Router, NavigationEnd } from '@angular/router';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, fromEvent, merge, timer, Subscription, interval, firstValueFrom } from 'rxjs';
import { switchMap, throttleTime, takeUntil, tap, filter } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { AuthGuardService } from 'src/app/services/auth-guard.service';
import { ToastrService } from 'ngx-toastr';
import { SESSION_LOGOUT } from 'src/app/constant/responseMessage';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {

  private readonly inactivityTimeout = 30 * 60 * 1000; // 30 minutes
  private readonly activityThrottleTime = 2 * 1000;   // 2 seconds
  private readonly wakeCheckInterval = 30 * 1000;     // wall-clock sweep, catches up after system sleep
  // shared by Auth, EDC (same origin): activity in either app keeps both sessions alive
  private readonly ACTIVITY_KEY = LAST_ACTIVITY_KEY;
  // only one tab (Auth or EDC) calls the logout API when several time out together
  private readonly LOGOUT_LOCK = 'cs-logout';

  private isMonitoring = false;
  private activitySource$ = new Subject<void>();
  private stopMonitoring$ = new Subject<void>();
  private timerSubscription: Subscription | null = null;

  private userActivityEvents$ = merge(
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'mousedown'),
    fromEvent(window, 'click'),
    fromEvent(window, 'keydown'),
    fromEvent(window, 'touchstart'),
    fromEvent(window, 'scroll'),
    fromEvent(window, 'wheel'),
    fromEvent(document, 'scroll', { capture: true }) // scrolling inside tables / modals
  ).pipe(
    throttleTime(this.activityThrottleTime)
  );

  public onTimeout$ = new Subject<void>();

  constructor(
    private router: Router,private cookieService: CookieService, 
    private ngZone: NgZone,
    private authGuard: AuthGuardService,
    private loginAPI: LoginService,
    private toastr: ToastrService
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const isAuth = (this as any).authGuard && (this as any).authGuard.isAuthorized && (this as any).authGuard.isAuthorized();
      if (isAuth) {
        if (!this.isMonitoring) this.startMonitoring();
      } else {
        if (this.isMonitoring) this.stopMonitoring();
      }
    });
  }

  public startMonitoring(): void {
    if (this.sessionExpired()) {
      this.checkAndLogout(); // don't revive a session that already timed out
      return;
    }
    this.isMonitoring = true;
    this.stopMonitoring$.next();
    this.updateLastActivity();

    // Outside Angular: mousemove / scroll / wheel fire constantly, and zone.js runs change detection for the whole app
    // after every one of them (throttleTime only limits the handler, not the zone). Nothing here changes the UI;
    // the logout paths re-enter the zone themselves via ngZone.run.
    this.ngZone.runOutsideAngular(() => {
      this.userActivityEvents$
        .pipe(
          filter(() => isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))),
          tap(() => this.sessionExpired() ? this.checkAndLogout() : this.resetTimer()), // first move after wake must not revive it
          takeUntil(this.stopMonitoring$)
        )
        .subscribe();

      fromEvent<StorageEvent>(window, 'storage')
        .pipe(takeUntil(this.stopMonitoring$))
        .subscribe((event) => {
          if (event.key === this.ACTIVITY_KEY && event.newValue !== LOGGED_OUT_VALUE) {
            this.activitySource$.next();
          } else if (event.key === this.ACTIVITY_KEY) {
            // another tab logged out (it wrote LOGGED_OUT): follow it, no second logout call
            this.ngZone.run(() => {
              this.stopMonitoring();
              this.finalizeLogout();
            });
          }
        });

      this.timerSubscription = this.activitySource$
        .pipe(
          filter(() => isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))),
          switchMap(() => timer(this.inactivityTimeout)),
          takeUntil(this.stopMonitoring$)
        )
        .subscribe(() => {
          this.checkAndLogout();
        });

      this.activitySource$.next();
    });

    // JS timers freeze while the OS sleeps (lid closed), so a pending timeout
    // keeps its remaining time instead of counting the sleep. Date.now() does
    // not freeze — this sweep re-checks elapsed time and logs out on wake.
    this.ngZone.runOutsideAngular(() => {
      interval(this.wakeCheckInterval)
        .pipe(
          filter(() => isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))),
          takeUntil(this.stopMonitoring$)
        )
        .subscribe(() => this.checkAndLogout());

      fromEvent(document, 'visibilitychange')
        .pipe(takeUntil(this.stopMonitoring$))
        .subscribe(() => {
          if (document.visibilityState === 'visible') this.checkAndLogout();
        });
    });
  }

  public stopMonitoring(): void {
    this.isMonitoring = false;
    this.stopMonitoring$.next();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  public resetTimer(): void {
    this.updateLastActivity();
    this.activitySource$.next();
  }

  /** The shared last activity is older than the timeout, e.g. the lid was closed or the browser reopened. */
  private sessionExpired(): boolean {
    const stored = localStorage.getItem(this.ACTIVITY_KEY);
    return stored !== null && stored !== LOGGED_OUT_VALUE && Date.now() - Number(stored) >= this.inactivityTimeout;
  }

  private updateLastActivity(): void {
    localStorage.setItem(this.ACTIVITY_KEY, Date.now().toString());
  }

  private checkAndLogout(): void {
    const stored = localStorage.getItem(this.ACTIVITY_KEY);
    if (stored === LOGGED_OUT_VALUE) {
      // another tab already logged out
      this.ngZone.run(() => {
        this.stopMonitoring();
        this.finalizeLogout();
      });
      return;
    }
    if (stored === null) {
      // a page cleared localStorage (not a logout): count from now instead of from 0
      this.resetTimer();
      return;
    }
    const lastActivity = Number(stored);
    const now = Date.now();

    if (now - lastActivity >= this.inactivityTimeout) {
      this.ngZone.run(() => {
        this.onTimeout$.next();
        this.stopMonitoring();
        if ((this as any).authGuard && (this as any).authGuard.isAuthorized) {
            if ((this as any).authGuard.isAuthorized()) {
                this.triggerLogout();
            }
        } else {
            this.triggerLogout();
        }
      });
    } else {
      this.activitySource$.next();
    }
  }

  private triggerLogout(): void {
    navigator.locks.request(this.LOGOUT_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock || !isNotEmptyString(this.cookieService.get(COOKIE_KEYS.USER_ID))) {
        this.ngZone.run(() => this.finalizeLogout()); // another tab is logging out, or the session is already gone
        return;
      }
      await firstValueFrom(this.loginAPI.logout()).catch(() => undefined);
      this.ngZone.run(() => this.finalizeLogout());
    });
  }

  logout(): void {
    // Terminate locally even if the server call fails — otherwise a failed
    // logout request would leave the session alive on the client.
    this.loginAPI.logout().subscribe({
      next: () => this.finalizeLogout(),
      error: () => this.finalizeLogout(),
    });
  }

  private finalizeLogout(): void {
    this.toastr.info(SESSION_LOGOUT);
    this.authGuard.logout();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.stopMonitoring$.complete();
    this.activitySource$.complete();
    this.onTimeout$.complete();
  }
}
